import React, { useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { FaCheckDouble, FaFileMedical, FaUser } from "react-icons/fa";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import CustomButton from "../../../shared/CustomButton";
import TextInput from "../../../shared/TextInput";
import { fetchData } from "../../../helpers/fetchData";
import { CalculateAge } from "../../../utils/CalculateAge";
import { extractTextFromHTML } from "../../../utils/ExtractTextFromHtml";
import CheckPermission from "../../../hooks/CheckPermission";

const referralPriorityOptions = ["Non-urgent", "Urgent", "Emergent"];

const getVitalValue = (emrData, category) => {
  const vitalSigns = Array.isArray(emrData?.vital_signs)
    ? emrData.vital_signs.flat()
    : [];
  return vitalSigns.find(v => v?.category === category)?.value || "";
};

const getBloodPressure = emrData => {
  const systolic = getVitalValue(emrData, "vital-sign-systolic");
  const diastolic = getVitalValue(emrData, "vital-sign-diastolic");
  return systolic || diastolic ? `${systolic}/${diastolic}` : "";
};

const latestText = records => {
  if (!Array.isArray(records)) return "";
  const last = records[records.length - 1];
  return extractTextFromHTML(last?.notes || last?.value || last || "");
};

const getDiagnosisText = emrData =>
  (emrData?.diagnosisArr || [])
    .map(d => d?.codeDescription || d?.diagnosis_text || d?.name)
    .filter(Boolean)
    .join(", ");

const toInfoLookup = source => {
  const id = source?.id || source?._id;
  return {
    ...(id ? { id } : {}),
    name_en: source?.name_en || "",
    name_ar: source?.name_ar || "",
  };
};

const toPractitionerInfo = practitioner => {
  const source = practitioner?.practitioner || practitioner || {};
  return {
    practitioner_id: source?.practitioner_id || source?.id || "",
    rcm_ref: source?.rcm_ref || 0,
    name: source?.name || source?.full_name || "",
    name_ar: source?.name_ar || "",
    document_id: source?.document_id || "",
    room_no: source?.room_no || "",
    ward_no: source?.ward_no || "",
    license: source?.license || source?.license_id || "",
    stamp: source?.stamp || "",
    signature: source?.signature || "",
    profile_photo: source?.profile_photo || source?.profile_image || "",
    department: toInfoLookup(source?.department),
    speciality: toInfoLookup(source?.speciality),
    extra_attachments: source?.extra_attachments || [],
    session_types: source?.session_types || [],
  };
};

const TextAreaField = ({ name, label, placeholder, error, touched }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1 col-span-2">
      <label className="input-label">{t(label)}</label>
      <Field as="textarea" name={name} className="textarea-field min-h-[84px]" placeholder={t(placeholder)} />
      {error && touched && <div className="text-red-500 text-sm">{error}</div>}
    </div>
  );
};

const CreateExternalReferralForm = ({
  emrData,
  patientData,
  referralData,
  isEdit,
  setOpenExternalReferralFormModal,
  setSelectEmrData,
  fetchInitialData,
}) => {
  const { hasPermission } = CheckPermission();
  const { t } = useTranslation();
  const { roleData } = useSelector(state => state?.auth);
  const [isLoading, setIsLoading] = useState(false);

  const patient = useMemo(
    () => referralData?.patient || patientData || emrData?.patient || {},
    [emrData?.patient, patientData, referralData?.patient]
  );
  const practitioner = useMemo(
    () => referralData?.practitioner || emrData?.practitioner || roleData || {},
    [emrData?.practitioner, referralData?.practitioner, roleData]
  );
  const practitionerInfo = useMemo(() => toPractitionerInfo(practitioner), [practitioner]);
  const external = useMemo(
    () => referralData?.external_referral || {},
    [referralData?.external_referral]
  );

  const initialValues = useMemo(() => ({
    visit_date: referralData?.visit_date || moment(emrData?.created_at || new Date()).format("YYYY-MM-DD"),
    age: referralData?.age || CalculateAge(patient?.dob) || "",
    allergic_to: external?.allergic_to || "",
    history_of_present_illness: external?.history_of_present_illness || latestText(emrData?.medical_assessments?.present_illness) || latestText(emrData?.medical_assessments?.chief_complaints),
    physical_examination: external?.physical_examination || referralData?.physical_examination || latestText(emrData?.medical_assessments?.significient_signs),
    temperature: referralData?.vitals?.temperature || getVitalValue(emrData, "vital-sign-temperature"),
    pulse_rate: referralData?.vitals?.pulse_rate || getVitalValue(emrData, "vital-sign-pulse"),
    respiratory_rate: referralData?.vitals?.respiratory_rate || getVitalValue(emrData, "vital-sign-respiratory-rate"),
    blood_pressure: referralData?.vitals?.blood_pressure || getBloodPressure(emrData),
    oxygen_saturation: referralData?.vitals?.oxygen_saturation || getVitalValue(emrData, "vital-sign-oxygen-saturation") || getVitalValue(emrData, "vital-sign-spo2"),
    investigations: referralData?.investigations || "",
    treatment_procedures_done: external?.treatment_procedures_done || referralData?.treatment_given || "",
    diagnosis: external?.diagnosis || referralData?.provisional_diagnosis || getDiagnosisText(emrData),
    referral_transfer_reason: external?.referral_transfer_reason || referralData?.reasons_for_referral || "",
    referral_priority: external?.referral_priority || referralData?.type_of_referral || "Non-urgent",
    treating_doctor_name: referralData?.treating_doctor_name || practitionerInfo?.name || "",
    treating_doctor_id: referralData?.treating_doctor_id || practitionerInfo?.practitioner_id || "",
  }), [emrData, external, patient?.dob, practitionerInfo, referralData]);

  const validationSchema = Yup.object().shape({
    visit_date: Yup.string().required(t("Date is required")),
    history_of_present_illness: Yup.string().trim().required(t("History of present illness is required")),
    diagnosis: Yup.string().trim().required(t("Diagnosis is required")),
    referral_transfer_reason: Yup.string().trim().required(t("Reason for referral is required")),
    referral_priority: Yup.string().trim().required(t("Type of referral is required")),
  });

  const handleSubmit = async values => {
    if (!hasPermission("referral_form", isEdit ? "update" : "create")) return;
    const payload = {
      id: referralData?.id,
      form_type: "external",
      patient,
      practitioner: practitionerInfo,
      emr_id: emrData?.emr_id || referralData?.emr_id,
      visit_id: String(emrData?.visit_id || referralData?.visit_id || ""),
      visit_date: values.visit_date,
      age: values.age,
      physical_examination: values.physical_examination,
      vitals: {
        blood_pressure: values.blood_pressure,
        temperature: values.temperature,
        respiratory_rate: values.respiratory_rate,
        pulse_rate: values.pulse_rate,
        oxygen_saturation: values.oxygen_saturation,
      },
      investigations: values.investigations,
      provisional_diagnosis: values.diagnosis,
      treatment_given: values.treatment_procedures_done,
      type_of_referral: values.referral_priority,
      reasons_for_referral: values.referral_transfer_reason,
      treating_doctor_name: values.treating_doctor_name,
      treating_doctor_id: values.treating_doctor_id,
      external_referral: {
        allergic_to: values.allergic_to,
        history_of_present_illness: values.history_of_present_illness,
        physical_examination: values.physical_examination,
        treatment_procedures_done: values.treatment_procedures_done,
        diagnosis: values.diagnosis,
        referral_transfer_reason: values.referral_transfer_reason,
        referral_priority: values.referral_priority,
        responsible_hcw: referralData?.external_referral?.responsible_hcw || [],
      },
      status: referralData?.status || "Issued",
    };

    setIsLoading(true);
    const response = await fetchData("POST", isEdit ? "/v3/referral-form/update" : "/v3/referral-form/create", payload);
    setIsLoading(false);
    if (response) {
      toast.success(response?.message);
      setOpenExternalReferralFormModal(false);
      setSelectEmrData?.(null);
      fetchInitialData?.();
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border p-3 rounded-lg bg-gray-100 mt-2">
        <div className="flex items-center gap-2"><FaUser /><b>{t("Patient Name")}:</b><span>{patient?.full_name || "--"}</span></div>
        <div className="flex items-center gap-2"><FaFileMedical /><b>{t("MRN")}:</b><span>{patient?.file_no || patient?.mrn || "--"}</span></div>
        <div className="flex items-center gap-2"><b>{t("Age")}:</b><span>{CalculateAge(patient?.dob) || "--"}</span></div>
      </div>

      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {props => (
          <Form>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-4">
              <TextInput name="visit_date" type="date" label={t("Date*")} placeholder={t("Select referral date")} />
              <TextInput name="allergic_to" label={t("Allergic To")} placeholder={t("NONE")} />
              <TextInput name="treating_doctor_name" label={t("Treating Physician")} placeholder={t("Treating physician name")} readOnly />

              <div className="col-span-2 grid grid-cols-1 md:grid-cols-5 gap-x-2">
                <TextInput name="temperature" label={t("Temp")} placeholder={t("36.5")} />
                <TextInput name="pulse_rate" label={t("PR")} placeholder={t("72")} />
                <TextInput name="respiratory_rate" label={t("Respiratory Rate")} placeholder={t("18")} />
                <TextInput name="blood_pressure" label={t("BP (mmHg)")} placeholder={t("120/80")} />
                <TextInput name="oxygen_saturation" label={t("O2 Sat")} placeholder={t("97")} />
              </div>

              <TextAreaField name="history_of_present_illness" label="History of Present Illness*" placeholder="Enter history of present illness" error={props.errors.history_of_present_illness} touched={props.touched.history_of_present_illness} />
              <TextAreaField name="physical_examination" label="Physical Examination" placeholder="Enter physical examination" error={props.errors.physical_examination} touched={props.touched.physical_examination} />

              <TextAreaField name="investigations" label="Investigations" placeholder="Enter investigations" error={props.errors.investigations} touched={props.touched.investigations} />
              <TextAreaField name="treatment_procedures_done" label="Treatment given and/or Procedures done" placeholder="Enter treatment or procedures done" error={props.errors.treatment_procedures_done} touched={props.touched.treatment_procedures_done} />
              <TextAreaField name="diagnosis" label="Diagnosis*" placeholder="Enter diagnosis" error={props.errors.diagnosis} touched={props.touched.diagnosis} />
              <TextAreaField name="referral_transfer_reason" label="Reason(s) for referral/transfer*" placeholder="Enter reason for referral or transfer" error={props.errors.referral_transfer_reason} touched={props.touched.referral_transfer_reason} />

              <div className="col-span-2">
                <label className="input-label">{t("Type of referral*")}</label>
                <div className="flex flex-wrap gap-5 rounded-md border border-gray-200 px-3 py-2 min-h-[38px]">
                  {referralPriorityOptions.map(option => (
                    <label key={option} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={props.values.referral_priority === option}
                        onChange={event => props.setFieldValue("referral_priority", event.target.checked ? option : "")}
                      />
                      <span>{t(option)}</span>
                    </label>
                  ))}
                </div>
                {props.errors.referral_priority && props.touched.referral_priority && <div className="text-red-500 text-sm">{props.errors.referral_priority}</div>}
              </div>
            </div>

            <div className="flex items-center justify-end mt-3">
              {hasPermission("referral_form", isEdit ? "update" : "create") && <CustomButton loading={isLoading} type="submit" icon={<FaCheckDouble size={12} className="me-2" />} label={isEdit ? t("Update External Referral Form") : t("Create External Referral Form")} buttonType="primary" />}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateExternalReferralForm;
